import React, { useCallback, useContext } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Field as FormikControl, Form, Formik } from "formik";
import classNames from "classnames";
import { Transaction } from "@common/types/transaction";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { ToggleableInput } from "@design-system/ToggleableInput/ToggleableInput";
import { Button } from "@design-system/Button/Button";
import { Input } from "@design-system/Input/Input";
import { Field } from "@design-system/Field/Field";
import { EditIcon } from "@icons";
import "./TransactionDetailsMemo.scss";
import { TransactionDetailsContext } from "./transactionDetails.context";

interface Props {
    transaction: Transaction;
    required: boolean;
}

interface FormSchema {
    memo: string;
}

export const TransactionDetailsMemo: React.FC<Props> = ({
    transaction,
    required,
}) => {
    const { onUpdate } = useContext(TransactionDetailsContext);
    const initialValues: FormSchema = { memo: transaction.memo ?? "" };
    const onSubmit = useCallback(
        async (values: FormSchema, toggleInput: () => void) => {
            toggleInput();
            if (transaction.memo !== values.memo) {
                await onUpdate({ memo: values.memo });
            }
        },
        [onUpdate, transaction.memo],
    );

    const tooltipText = transaction.memo
        ? "Edit memo for this transaction"
        : "Add a memo to this transaction";

    return (
        <div
            className={classNames("transaction-details-memo mt-3 mb-3", {
                "transaction-details-memo--required":
                    !transaction.memo && required,
            })}
        >
            <ToggleableInput
                renderDefaultState={(toggleInput) => (
                    <Tooltip content={tooltipText}>
                        <div
                            data-testid="transaction-details-note-container"
                            className={classNames(
                                "transaction-details-memo__display",
                                {
                                    "transaction-details-memo__display--filled":
                                        Boolean(transaction.memo),
                                    "transaction-details-memo__display--empty":
                                        !transaction.memo,
                                },
                            )}
                            {...toggleInput}
                        >
                            <div>
                                {transaction.memo ? (
                                    <span className="label">Memo</span>
                                ) : (
                                    <span>Add memo</span>
                                )}
                                {transaction.memo && (
                                    <div data-testid="transaction-note">
                                        {transaction.memo}{" "}
                                    </div>
                                )}
                            </div>
                            <aside className="ml-auto">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    iconOnly
                                    aria-label="Edit memo"
                                >
                                    <EditIcon className="transaction-details-memo__edit-icon" />
                                </Button>
                            </aside>
                        </div>
                    </Tooltip>
                )}
                renderInputState={(toggleInput, inputRef) => (
                    <Formik
                        initialValues={initialValues}
                        onSubmit={(values) =>
                            onSubmit(values, toggleInput.onClick)
                        }
                    >
                        {({ submitForm, values }) => (
                            <Form>
                                <Field.Root>
                                    <FormikControl
                                        as={Input}
                                        data-testid="transaction-note-input"
                                        name="memo"
                                        size="xl"
                                        className={classNames("mb-0", {
                                            empty: !values.memo,
                                        })}
                                        ref={inputRef}
                                        onBlur={() => {
                                            submitForm();
                                        }}
                                    />
                                </Field.Root>
                            </Form>
                        )}
                    </Formik>
                )}
            />
        </div>
    );
};
